if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        // Variant-image gallery filter: keep media not linked to any variant + the
        // selected variant's linked image; hide other variants' images. Safe no-op
        // when the product has no variant-linked media.
        this.captureCanonicalMedia();
        this.applyInitialVariantFilter();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        // Build the zoom dataSource from the full (canonical) media set so zoom indices
        // stay stable even when the visible gallery is filtered down by variant.
        const allMedia =
          this.allMainItems && this.allMainItems.length
            ? this.allMainItems
            : [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      captureCanonicalMedia() {
        if (this.canonicalCaptured) return;
        this.mainListEl = this.elements.mediaList;
        this.thumbListEl = this.querySelector('[id^="Slider-Thumbnails"]');
        this.allMainItems = this.mainListEl
          ? Array.from(this.mainListEl.querySelectorAll('.product__media-item'))
          : [];
        this.allThumbItems = this.thumbListEl
          ? Array.from(this.thumbListEl.querySelectorAll('.product__thumbs-item'))
          : [];
        this.hasVariantFilter = this.allMainItems.some((el) => (el.dataset.variantIds || '').trim() !== '');
        this.canonicalCaptured = true;
      }

      mediaMatchesVariant(el, variantId) {
        const ids = (el.dataset.variantIds || '').trim();
        if (!ids) return true; // not linked to any variant -> always keep
        return ids.split(/\s+/).indexOf(String(variantId)) !== -1;
      }

      rebuildMediaLists(keepFn) {
        if (this.mainListEl && this.allMainItems) {
          this.mainListEl.innerHTML = '';
          this.allMainItems.forEach((el) => {
            if (keepFn(el)) {
              el.removeAttribute('hidden');
              this.mainListEl.appendChild(el);
            }
          });
        }
        if (this.thumbListEl && this.allThumbItems) {
          this.thumbListEl.innerHTML = '';
          this.allThumbItems.forEach((el) => {
            if (keepFn(el)) {
              el.removeAttribute('hidden');
              this.thumbListEl.appendChild(el);
            }
          });
        }
      }

      restoreAllMedia() {
        this.rebuildMediaLists(() => true);
        this.currentFilterKey = '__all__';
      }

      applyInitialVariantFilter() {
        if (this.initialFiltered) return;
        this.initialFiltered = true;
        if (!this.hasVariantFilter) return;
        try {
          // Server marks non-matching media for the initial variant with [hidden].
          this.rebuildMediaLists((el) => !el.hasAttribute('hidden'));
          this.currentFilterKey = this.allMainItems
            .filter((el) => !el.hasAttribute('hidden'))
            .map((el) => el.dataset.mediaId)
            .join(',');
        } catch (e) {
          this.restoreAllMedia();
        }
      }

      destroySliders() {
        if (typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          try {
            this.sliderInstance.slider.destroy(true, true);
          } catch (e) {}
        }
        this.sliderInstance = false;
        if (typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider) {
          try {
            this.thumbsInstance.slider.destroy(true, true);
          } catch (e) {}
        }
        this.thumbsInstance = false;
      }

      reinitSliders() {
        this.updateMediaLayout();
      }

      filterToVariant(variant) {
        const vid = String(variant.id);
        const keepFn = (el) => this.mediaMatchesVariant(el, vid);

        const kept = this.allMainItems.filter(keepFn);
        // Never empty the gallery: if nothing matches, show everything.
        if (kept.length === 0) {
          if (this.currentFilterKey === '__all__') return;
          this.destroySliders();
          this.restoreAllMedia();
          this.reinitSliders();
          return;
        }

        const key = kept.map((el) => el.dataset.mediaId).join(',');
        if (key === this.currentFilterKey) return; // visible set unchanged -> just slide
        this.currentFilterKey = key;

        this.destroySliders();
        this.rebuildMediaLists(keepFn);
        this.reinitSliders();
      }

      setActiveMedia(variant) {
        // Variant-image filter: show media not linked to any variant + the selected
        // variant's linked image; hide media linked to other variants. Any failure
        // falls back to the full unfiltered gallery so it never breaks.
        if (this.hasVariantFilter && variant) {
          try {
            this.filterToVariant(variant);
          } catch (e) {
            this.restoreAllMedia();
            this.reinitSliders();
          }

          // Quick view re-renders product info right after updateMedia (variant:changed
          // event + the variantChange pubsub subscribers), which can revert this gallery's
          // DOM back to the default variant. Re-apply the filter once that chain settles.
          // Idempotent (only acts if the visible set diverged) and scoped to quick view so
          // the PDP keeps a single clean filter.
          if (this.context === 'quickview') {
            const reapplyVariantFilter = () => {
              try {
                const vid = String(variant.id);
                const desired = this.allMainItems
                  .filter((el) => this.mediaMatchesVariant(el, vid))
                  .map((el) => el.dataset.mediaId)
                  .join(',');
                if (!desired || !this.mainListEl) return;
                const current = Array.prototype.slice
                  .call(this.mainListEl.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)'))
                  .map((el) => el.dataset.mediaId)
                  .join(',');
                if (desired !== current) {
                  this.currentFilterKey = null;
                  this.filterToVariant(variant);
                }
              } catch (e) {}
            };
            requestAnimationFrame(reapplyVariantFilter);
            setTimeout(reapplyVariantFilter, 250);
          }
        }

        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance && this.sliderInstance.slider) {
          let slideIndex = -1;
          if (this.mainListEl) {
            // Index among non-duplicate slides (Swiper's loop clones share the same
            // data-media-id), which is what slideToLoop expects.
            const originals = Array.prototype.slice.call(
              this.mainListEl.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')
            );
            for (let i = 0; i < originals.length; i++) {
              if (originals[i].dataset.mediaId == variant.featured_media.id) {
                slideIndex = i;
                break;
              }
            }
          }
          if (slideIndex < 0) slideIndex = (variant.featured_media.position || 1) - 1;
          this.sliderInstance.slider.slideToLoop(slideIndex);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
